import {useEffect, useState} from "react"
import {Route, Switch, useHistory} from "react-router-dom"
import {ProgressSpinner} from "primereact/progressspinner"
import api from "./api"
import userContext from "./context/userContext"
import Navbar from "./component/Navbar/Navbar"
import Breadcrumb from "./component/Breadcrumb/Breadcrumb"
import Auth from "./component/Auth/Auth"
import Homepage from "./component/Homepage/Homepage"
import TaskList from "./component/Task/TaskList"
import EnumMachineList from "./component/Enum/Machine/EnumMachineList"
import TaskPreventionForm from "./component/Task/Prevention/TaskPreventionForm"
import TaskPreventionSubTaskForm from "./component/Task/Prevention/TaskPreventionSubTaskForm"
import TaskReportForm from "./component/Task/Report/TaskReportForm"
import TaskReportFinishForm from "./component/Task/Report/TaskReportFinishForm"
import TaskMachineList from "./component/Task/Machine/TaskMachineList"
import EnumMachineForm from "./component/Enum/Machine/EnumMachineForm"
import EnumMachineTypeList from "./component/Enum/MachineType/EnumMachineTypeList"
import EnumMachineTypeForm from "./component/Enum/MachineType/EnumMachineTypeForm"
import EnumUserList from "./component/Enum/Users/EnumUserList"
import EnumUserForm from "./component/Enum/Users/EnumUserForm"
import EnumTeamList from "./component/Enum/Team/EnumTeamList"
import EnumTeamForm from "./component/Enum/Team/EnumTeamForm"
import EnumOperationList from "./component/Enum/Operation/EnumOperationList"
import EnumOperationForm from "./component/Enum/Operation/EnumOperationForm"
import EnumPlaceList from "./component/Enum/Place/EnumPlaceList"
import EnumPlaceForm from "./component/Enum/Place/EnumPlaceForm"
import EnumWarehouseSupplierList
  from "./component/Enum/Warehouse/Supplier/EnumWarehouseSupplierList"
import EnumWarehouseSupplierForm
  from "./component/Enum/Warehouse/Supplier/EnumWarehouseSupplierForm"
import EnumTaskTagList from "./component/Enum/TaskTag/EnumTaskTagList"
import EnumTaskTagForm from "./component/Enum/TaskTag/EnumTaskTagForm"
import EnumWarehouseWarehouseList
  from "./component/Enum/Warehouse/Warehouse/EnumWarehouseWarehouseList"
import EnumWarehouseWarehouseForm
  from "./component/Enum/Warehouse/Warehouse/EnumWarehouseWarehouseForm"
import EnumWarehouseCategoryList
  from "./component/Enum/Warehouse/Category/EnumWarehouseCategoryList"
import EnumWarehouseCategoryForm
  from "./component/Enum/Warehouse/Category/EnumWarehouseCategoryForm"
import EnumWarehousePositionList
  from "./component/Enum/Warehouse/Position/EnumWarehousePositionList"
import EnumWarehousePositionForm
  from "./component/Enum/Warehouse/Position/EnumWarehousePositionForm"
import EnumWarehousePartList from "./component/Enum/Warehouse/Part/EnumWarehousePartList"
import EnumWarehousePartForm from "./component/Enum/Warehouse/Part/EnumWarehousePartForm"
import WarehouseList from "./component/Warehouse/WarehouseList"
import WarehouseForm from "./component/Warehouse/WarehouseForm"
import NotificationList from "./component/Notification/NotificationList"
import NotificationProvider from "./component/Notification/NotificationProvider"
import Footer from "./component/Footer/Footer"
import TaskReportDetail from "./component/Task/Report/Detail/TaskReportDetail"
import TaskReportRemind from "./component/Task/Report/Remind/Remind"
import TaskManagementList from "./component/Task/Management/TaskManagementList"
import TaskManagementForm from "./component/Task/Management/TaskManagementForm"
import TaskManagementFinishForm from "./component/Task/Management/TaskManagementFinishForm"

export default function App() {
  const history = useHistory()
  const [user, setUser] = useState({})
  const [isAuthenticatedEndpointCalled, setIsAuthenticatedEndpointCalled] = useState(false)

  const login = ({user, pass}) => api
    .post("/login", {user, pass})
    .then(({data}) => {
      api.setToken(data.data.token)
      setUser(data.data)
      history.goBack()
    })

  const logout = () => {
    api.removeToken()
    setUser({})
    history.push("/login")
  }

  const hasRole = (name) => user.role === name

  const hasPerm = (name) => user.permissions && user.permissions.indexOf(name) > -1

  const isAdmin = () => hasRole("ADMIN") || hasRole("SUPERADMIN")

  useEffect(() => {
    api.get("/is-authenticated")
      .then(({data}) => setUser(data.data))
      .catch(() => history.push("/login"))
      .finally(() => setIsAuthenticatedEndpointCalled(true))
  }, [history])

  if (!isAuthenticatedEndpointCalled) {
    return (
      <ProgressSpinner style={{
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translateX(-50%) translateY(-50%)",
      }}/>
    )
  }

  return (
    <userContext.Provider value={{user, login, logout, hasRole, hasPerm, isAdmin}}>
      <div className={"grid"}>
        {user.uuid && (
          <>
            <NotificationProvider>
              <NotificationList/>
              <Navbar/>
            </NotificationProvider>
            <Breadcrumb/>
          </>
        )}

        <Switch>
          {/* misc */}
          <Route path={"/login"} exact={true}>
            <Auth/>
          </Route>

          {/* tasks */}
          <Route path={"/tasks"} exact={true}>
            <TaskList/>
          </Route>
          <Route path={"/tasks/machines"} exact={true}>
            <TaskMachineList/>
          </Route>
          <Route path={"/task/prevention/:uuid/subtasks"} exact={true}>
            <TaskPreventionSubTaskForm/>
          </Route>
          <Route path={"/task/prevention/:uuid"} exact={true}>
            <TaskPreventionForm/>
          </Route>
          <Route path={"/task/prevention"} exact={true}>
            <TaskPreventionForm/>
          </Route>
          <Route path={"/task/report/:uuid/finish/:isPossible"} exact={true}>
            <TaskReportFinishForm/>
          </Route>
          <Route path={"/task/report/:uuid/detail"} exact={true}>
            <TaskReportDetail/>
          </Route>
          <Route path={"/task/report/:uuid/remind"} exact={true}>
            <TaskReportRemind/>
          </Route>
          <Route path={"/task/report/:uuid"} exact={true}>
            <TaskReportForm/>
          </Route>
          <Route path={"/task/report"} exact={true}>
            <TaskReportForm/>
          </Route>

          {/* management tasks */}
          <Route path={"/task/management/:uuid/finish/:isPossible"} exact={true}>
            <TaskManagementFinishForm/>
          </Route>
          <Route path={"/task/management/:uuid"} exact={true}>
            <TaskManagementForm/>
          </Route>
          <Route path={"/task/management"} exact={true}>
            <TaskManagementForm/>
          </Route>
          <Route path={"/tasks/management"} exact={true}>
            <TaskManagementList/>
          </Route>

          {/* enum machines */}
          <Route path={"/enum/machines"} exact={true}>
            <EnumMachineList/>
          </Route>
          <Route path={"/enum/machine/:uuid"} exact={true}>
            <EnumMachineForm/>
          </Route>
          <Route path={"/enum/machine"} exact={true}>
            <EnumMachineForm/>
          </Route>

          {/* enum machine types */}
          <Route path={"/enum/machine-types"} exact={true}>
            <EnumMachineTypeList/>
          </Route>
          <Route path={"/enum/machine-type/:uuid"} exact={true}>
            <EnumMachineTypeForm/>
          </Route>
          <Route path={"/enum/machine-type"} exact={true}>
            <EnumMachineTypeForm/>
          </Route>

          {/* enum users */}
          <Route path={"/enum/users"} exact={true}>
            <EnumUserList/>
          </Route>
          <Route path={"/enum/user/:uuid"} exact={true}>
            <EnumUserForm/>
          </Route>
          <Route path={"/enum/user"} exact={true}>
            <EnumUserForm/>
          </Route>

          {/* enum teams */}
          <Route path={"/enum/teams"} exact={true}>
            <EnumTeamList/>
          </Route>
          <Route path={"/enum/team/:uuid"} exact={true}>
            <EnumTeamForm/>
          </Route>
          <Route path={"/enum/team"} exact={true}>
            <EnumTeamForm/>
          </Route>

          {/* enum operations */}
          <Route path={"/enum/operations"} exact={true}>
            <EnumOperationList/>
          </Route>
          <Route path={"/enum/operation/:uuid"} exact={true}>
            <EnumOperationForm/>
          </Route>
          <Route path={"/enum/operation"} exact={true}>
            <EnumOperationForm/>
          </Route>

          {/* enum places */}
          <Route path={"/enum/places"} exact={true}>
            <EnumPlaceList/>
          </Route>
          <Route path={"/enum/place/:uuid"} exact={true}>
            <EnumPlaceForm/>
          </Route>
          <Route path={"/enum/place"} exact={true}>
            <EnumPlaceForm/>
          </Route>

          {/* enum suppliers */}
          <Route path={"/enum/warehouse/suppliers"} exact={true}>
            <EnumWarehouseSupplierList/>
          </Route>
          <Route path={"/enum/warehouse/supplier/:uuid"} exact={true}>
            <EnumWarehouseSupplierForm/>
          </Route>
          <Route path={"/enum/warehouse/supplier"} exact={true}>
            <EnumWarehouseSupplierForm/>
          </Route>

          {/* enum task tags */}
          <Route path={"/enum/task-tags"} exact={true}>
            <EnumTaskTagList/>
          </Route>
          <Route path={"/enum/task-tag/:uuid"} exact={true}>
            <EnumTaskTagForm/>
          </Route>
          <Route path={"/enum/task-tag"} exact={true}>
            <EnumTaskTagForm/>
          </Route>

          {/* enum warehouse warehouse */}
          <Route path={"/enum/warehouse/warehouses"}>
            <EnumWarehouseWarehouseList/>
          </Route>
          <Route path={"/enum/warehouse/warehouse/:uuid"}>
            <EnumWarehouseWarehouseForm/>
          </Route>
          <Route path={"/enum/warehouse/warehouse"}>
            <EnumWarehouseWarehouseForm/>
          </Route>

          {/* enum warehouse category */}
          <Route path={"/enum/warehouse/categories"}>
            <EnumWarehouseCategoryList/>
          </Route>
          <Route path={"/enum/warehouse/category/:uuid"}>
            <EnumWarehouseCategoryForm/>
          </Route>
          <Route path={"/enum/warehouse/category"}>
            <EnumWarehouseCategoryForm/>
          </Route>

          {/* enum warehouse position */}
          <Route path={"/enum/warehouse/positions"}>
            <EnumWarehousePositionList/>
          </Route>
          <Route path={"/enum/warehouse/position/:uuid"}>
            <EnumWarehousePositionForm/>
          </Route>
          <Route path={"/enum/warehouse/position"}>
            <EnumWarehousePositionForm/>
          </Route>

          {/* enum warehouse part */}
          <Route path={"/enum/warehouse/parts"}>
            <EnumWarehousePartList/>
          </Route>
          <Route path={"/enum/warehouse/part/:uuid"}>
            <EnumWarehousePartForm/>
          </Route>
          <Route path={"/enum/warehouse/part"}>
            <EnumWarehousePartForm/>
          </Route>

          {/* warehouse */}
          <Route path={"/warehouses"} exact={true}>
            <WarehouseList/>
          </Route>
          <Route path={"/warehouse/:uuid"} exact={true}>
            <WarehouseForm/>
          </Route>
          <Route path={"/warehouse"} exact={true}>
            <WarehouseForm/>
          </Route>

          <Route path={"/"} exact={true}>
            <Homepage/>
          </Route>
        </Switch>
      </div>

      <Footer/>
    </userContext.Provider>
  )
}
